import axios from 'axios';

// API base: the build-time env var wins, but the fallback must never be a bare
// localhost URL. In production the API shares this page's origin (a proxy routes
// /api to the backend), so falling back to location.origin self-heals a missing
// env var. Falling back to localhost instead pointed every visitor's browser at
// their OWN machine and broke all remote logins for ~13h on 2026-07-10.
// Only a page actually served from localhost targets the dev backend on :8000.
const isLocalDev = ['localhost', '127.0.0.1'].includes(window.location.hostname);

export const API_URL =
  process.env.REACT_APP_API_URL ||
  (isLocalDev ? 'http://localhost:8000' : window.location.origin);

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Redirect to login on expired/invalid token
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: async (email, password) => {
    const formData = new FormData();
    formData.append('username', email);
    formData.append('password', password);

    const response = await api.post('/api/auth/login', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  register: async (userData) => {
    const response = await api.post('/api/auth/register', userData);
    return response.data;
  },
  me: async () => {
    const response = await api.get('/api/auth/me');
    return response.data;
  },
  listUsers: async () => {
    const response = await api.get('/api/auth/users');
    return response.data;
  },
  createUser: async (userData) => {
    const response = await api.post('/api/auth/register', userData);
    return response.data;
  },
  setUserActive: async (userId, isActive) => {
    const response = await api.patch(`/api/auth/users/${userId}/active`, { is_active: isActive });
    return response.data;
  },
  setUserAdmin: async (userId, isAdmin) => {
    const response = await api.patch(`/api/auth/users/${userId}/admin`, { is_admin: isAdmin });
    return response.data;
  },
  setUserRole: async (userId, role) => {
    const response = await api.patch(`/api/auth/users/${userId}/role`, { role });
    return response.data;
  },
  resetUserPassword: async (userId, newPassword) => {
    const response = await api.post(`/api/auth/users/${userId}/reset-password`, { new_password: newPassword });
    return response.data;
  },
  changeMyPassword: async (currentPassword, newPassword) => {
    const response = await api.post('/api/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
    return response.data;
  },
};

// Candidate APIs
export const candidateAPI = {
  api: api,  // Expose the api instance for direct use
  // One of the candidate's Drive documents, streamed through the backend as a
  // Blob (see GET /{id}/document-file/{kind}). Used for roles with no personal
  // Drive access. The session travels in the Authorization header, not the URL.
  getDriveFolder: (id) => api.get(`/api/candidates/${id}/drive-folder`),
  // "Progress to RVN": creates the new linked record for a recruited student /
  // trainee nurse and returns it (see POST /{id}/progress).
  progress: async (id, position = 'rvn') => {
    const response = await api.post(`/api/candidates/${id}/progress`, { position });
    return response.data;
  },
  fetchDocumentFile: async (id, kind, docKey = null) => {
    const response = await api.get(`/api/candidates/${id}/document-file/${kind}`, {
      params: docKey ? { doc_key: docKey } : {},
      responseType: 'blob',
    });
    return response.data;
  },
  // view: 'active' (default, hides archived) | 'archived' | 'all'.
  // Filtering by a terminal status always includes archived candidates — see the
  // list endpoint. Omitted when 'active' so the URL stays clean.
  list: async (statusFilter = null, view = null) => {
    const params = {};
    if (statusFilter) params.status_filter = statusFilter;
    if (view && view !== 'active') params.view = view;
    const response = await api.get('/api/candidates/', { params });
    return response.data;
  },
  stats: async () => {
    const response = await api.get('/api/candidates/stats');
    return response.data;
  },
  archive: async (id) => {
    const response = await api.post(`/api/candidates/${id}/archive`);
    return response.data;
  },
  restore: async (id) => {
    const response = await api.post(`/api/candidates/${id}/restore`);
    return response.data;
  },
  get: async (id) => {
    const response = await api.get(`/api/candidates/${id}`);
    return response.data;
  },
  create: async (candidateData) => {
    const response = await api.post('/api/candidates/', candidateData);
    return response.data;
  },
  // HR uploads a CV or cover letter (kind: 'cv' | 'cover_letter') into the
  // candidate's Drive folder. Multipart, so the browser sets the boundary itself.
  uploadDocument: async (id, kind, file) => {
    const form = new FormData();
    form.append('kind', kind);
    form.append('file', file);
    const response = await api.post(`/api/candidates/${id}/upload-document`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  // "Attach from email": document attachments in recent emails (the caller's
  // own inbox + HR's), filtered by the candidate's address unless q is given.
  listEmailAttachments: async (id, q = '') => {
    const response = await api.get(`/api/candidates/${id}/email-attachments`, {
      params: q ? { q } : {},
    });
    return response.data;
  },
  // File ONE of those attachments as the CV or cover letter.
  attachFromEmail: async (id, { inbox, message_id, filename, kind }) => {
    const response = await api.post(`/api/candidates/${id}/attach-from-email`, {
      inbox, message_id, filename, kind,
    });
    return response.data;
  },
  // Copy the position's interview-questions Doc into the candidate's folder.
  // 409 when one already exists unless replace=true (the caller confirms first).
  createInterviewForm: async (id, replace = false) => {
    const response = await api.post(`/api/candidates/${id}/interview-form`, null, {
      params: replace ? { replace: true } : {},
    });
    return response.data;
  },
  update: async (id, candidateData) => {
    const response = await api.patch(`/api/candidates/${id}`, candidateData);
    return response.data;
  },
  // A 30-minute token that can only render this candidate's view panes (the
  // form / RCVS / night-worker iframes and tabs, which cannot send a header).
  // Never put the login token in a URL — it ends up in the server logs.
  paneToken: async (id) => {
    const response = await api.post(`/api/candidates/${id}/pane-token`);
    return response.data.token;
  },
  delete: async (id) => {
    const response = await api.delete(`/api/candidates/${id}`);
    return response.data;
  },
  previewOffer: async (id, offerData) => {
    const response = await api.post(`/api/candidates/${id}/preview-offer`, offerData);
    return response.data;
  },
  // Every position's standard "Hours of work" contract wording, with the
  // {{ hours_per_week }} token left in for the offer form to fill as hours are
  // typed. Static per deploy; fetched once.
  hoursOfWorkDefaults: async () => {
    const response = await api.get('/api/candidates/hours-of-work-defaults');
    return response.data;
  },
  // The Hours of work block exactly as the next generated contract will carry
  // it, plus a warning when it disagrees with Hours per Week on the record.
  contractHoursOfWork: async (id) => {
    const response = await api.get(`/api/candidates/${id}/contract/hours-of-work`);
    return response.data;
  },
  sendOffer: async (id, offerData, customContent = null) => {
    const payload = { ...offerData };
    if (customContent) {
      payload.custom_content = customContent;
    }
    const response = await api.post(`/api/candidates/${id}/send-offer`, payload);
    return response.data;
  },
  addReference: async (id, referenceData) => {
    const response = await api.post(`/api/candidates/${id}/references`, referenceData);
    return response.data;
  },
  sendReferenceRequest: async (candidateId, referenceId) => {
    const response = await api.post(
      `/api/candidates/${candidateId}/references/${referenceId}/send-request`
    );
    return response.data;
  },
  getReferences: async (id) => {
    const response = await api.get(`/api/candidates/${id}/references`);
    return response.data;
  },
  previewDocuments: async (id) => {
    const response = await api.post(`/api/candidates/${id}/preview-documents`);
    return response.data;
  },
  sendDocuments: async (id, customContent = null) => {
    const payload = customContent ? { custom_content: customContent } : {};
    const response = await api.post(`/api/candidates/${id}/send-documents`, payload);
    return response.data;
  },
  getTaskStatus: async (id, taskId) => {
    const response = await api.get(`/api/candidates/${id}/task-status/${taskId}`);
    return response.data;
  },
  previewContractEmail: async (id) => {
    const response = await api.post(`/api/candidates/${id}/preview-contract-email`);
    return response.data;
  },
  // Rejection / Offer Declined email — sent FROM the logged-in user, not Sarah.
  // Only answers for a candidate already in one of those two statuses.
  previewOutcomeEmail: async (id, outcome) => {
    const response = await api.post(`/api/candidates/${id}/preview-outcome-email`, null, { params: outcome ? { outcome } : {} });
    return response.data;
  },
  sendOutcomeEmail: async (id, customContent) => {
    const response = await api.post(`/api/candidates/${id}/send-outcome-email`, { custom_content: customContent });
    return response.data;
  },
  notifyClaire: async (id) => {
    const response = await api.post(`/api/candidates/${id}/notify-claire`);
    return response.data;
  },
  // PART 5 - Induction Folder APIs
  getInduction: async (id) => {
    const response = await api.get(`/api/candidates/${id}/induction`);
    return response.data;
  },
  generateInductionDocument: async (id, docKey) => {
    const response = await api.post(`/api/candidates/${id}/induction/${docKey}/generate`);
    return response.data;
  },
  // SignWell only: the live signing links of the open request (contract or
  // induction bundle), for handing to a candidate whose SignWell email went to junk.
  getEsignSigningLinks: async (id, kind) => {
    const response = await api.get(`/api/candidates/${id}/esign/signing-links`, { params: { kind } });
    return response.data;
  },
  // SignWell only: generate every unsigned eSignature document and send them as
  // ONE SignWell request (replaces the manual Google eSignature launch).
  sendInductionEsignatureRequest: async (id) => {
    const response = await api.post(`/api/candidates/${id}/induction/send-esignature-request`);
    return response.data;
  },
  verifyInductionRequests: async (id) => {
    const response = await api.post(`/api/candidates/${id}/induction/verify-requests`);
    return response.data;
  },
  previewInductionEmail: async (id) => {
    const response = await api.post(`/api/candidates/${id}/induction/preview-email`);
    return response.data;
  },
  sendInductionEmail: async (id, customContent = null) => {
    const payload = customContent ? { custom_content: customContent } : {};
    const response = await api.post(`/api/candidates/${id}/induction/send-email`, payload);
    return response.data;
  },
  updateInductionFolderStatus: async (id, created) => {
    const response = await api.patch(`/api/candidates/${id}/induction/folder-created`, { created });
    return response.data;
  },

  // PART 6 - Training Log, filled in the app during training and signed at the end
  getTrainingLog: async (id) => {
    const response = await api.get(`/api/candidates/${id}/training-log`);
    return response.data;
  },
  tickTrainingLog: async (id, taskId, column, ticked) => {
    const response = await api.post(`/api/candidates/${id}/training-log/tick`, { task_id: taskId, column, ticked });
    return response.data;
  },
  requestTrainingLogSignature: async (id) => {
    const response = await api.post(`/api/candidates/${id}/training-log/request-signature`);
    return response.data;
  },
};

// Server-side config the UI renders from, so names/addresses that live in the
// backend's settings are never hardcoded in React (e.g. the Accounts
// Administrator's name — if the role changes hands the UI follows automatically).
export const configAPI = {
  get: async () => {
    const response = await api.get('/api/app-config');
    return response.data;
  },
};

export default api;
